import type { DateTime } from "luxon";
import type { NurseInShift } from "../../utils/models";
import { blockWidthPct, hidesActionsUntilHover, slotLayout } from "./DailyRow/blockLayout";
import type { CalculatedDayViewGaps, DayViewRowSlot } from "./utils";

type DayViewShiftItem = Extract<DayViewRowSlot["shiftsAndGaps"][number], { specificType: "shift" }>;

type Input = {
	calculatedData: CalculatedDayViewGaps | null;
	/** The location rows in the order they render, collapsed groups already left out (their rows are unmounted). */
	renderedLocationKeys: readonly string[];
	date: DateTime;
	dayStartHour: number;
	/** Whether removing this shift opens a call-off with the replacement search. */
	offersReplacementSearch: (shift: NurseInShift) => boolean;
	now: DateTime;
};

/**
 * The ONE Gaps-layout block whose Remove quick action the call-off walkthrough spotlights: the first
 * rendered qualifying block that keeps its actions mounted and hasn't started, else the latest-started one
 * (a started shift leaves the call-off window within the after-start allowance, taking the checkbox with it
 * mid-tour, so the one that started last has the most time left).
 *
 * Narrow blocks mount their actions only while hovered, so they are skipped — an anchor that exists only
 * under the pointer can't be spotlit. So are blocks that render no actions at all (optimistic) and rows
 * already pending deletion. Returned by identity rather than id so it can match exactly one element even
 * if a shift were ever listed twice. Null ⇒ no anchor, and the tour is refused.
 */
export const gapsCallOffAnchorItem = ({
	calculatedData,
	renderedLocationKeys,
	date,
	dayStartHour,
	offersReplacementSearch,
	now,
}: Input): DayViewShiftItem | null => {
	if (calculatedData === null) return null;
	// DEFAULT: with nothing upcoming, the latest-started block — the most of the after-start window left, as
	// the launch resolver picks its day. Strict `>` keeps the first in render order on a tie.
	let latestStarted: DayViewShiftItem | null = null;
	for (const locationKey of renderedLocationKeys) {
		const row = calculatedData[locationKey];
		if (row === undefined) continue;
		for (const slot of row.rows) {
			const layout = slotLayout(slot.start, date, dayStartHour);
			for (const item of slot.shiftsAndGaps) {
				if (item.specificType !== "shift") continue;
				const shift = item.value;
				if (shift.isOptimisticLoading || shift.isPendingToDelete) continue;
				// The same start clamp and end the rendered block uses (see DayViewGaps' shift element).
				const normStart = item.normalizedStartTime < layout.dayStart ? layout.dayStart : item.normalizedStartTime;
				if (hidesActionsUntilHover(blockWidthPct(normStart, item.normalizedEndTime, layout), false)) continue;
				if (!offersReplacementSearch(shift)) continue;
				if (shift.startTime > now) return item;
				if (latestStarted === null || shift.startTime > latestStarted.value.startTime) latestStarted = item;
			}
		}
	}
	return latestStarted;
};
